import React, { useEffect, useRef, useState } from "react";
import { Search, Building2, Truck, UserRound, CornerDownLeft } from "lucide-react";
import api from "@/lib/api";
import { matches } from "@/components/SearchBox";
import { setJump } from "@/lib/jump";

const KINDS = [
  { key: "customers", label: "Müşteri", icon: Building2, tab: "customers", url: "/customers" },
  { key: "suppliers", label: "Tedarikçi", icon: Truck, tab: "suppliers", url: "/suppliers" },
  { key: "staff", label: "Personel", icon: UserRound, tab: "staff", url: "/staff" },
];

export default function GlobalSearch({ goTo }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [hi, setHi] = useState(0);
  const box = useRef(null);

  useEffect(() => {
    if (!open || data) return;
    Promise.all(KINDS.map((k) => api.get(k.url).then((r) => r.data).catch(() => []))).then((res) => setData(Object.fromEntries(KINDS.map((k, i) => [k.key, res[i]]))));
  }, [open, data]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); box.current?.querySelector("input")?.focus(); setOpen(true); } };
    document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h);
  }, []);

  const results = q.trim() && data
    ? KINDS.flatMap((k) => (data[k.key] || []).filter((x) => matches(q, x.name, x.short_name, x.contact, x.phone)).slice(0, 6).map((x) => ({ kind: k, item: x })))
    : [];
  useEffect(() => { setHi(0); }, [q]);

  const pick = (r) => {
    setJump({ tab: r.kind.tab, id: r.item.id, name: r.item.short_name || r.item.name });
    setData(null); setQ(""); setOpen(false); goTo(r.kind.tab);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && results[hi]) pick(results[hi]);
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={box} className="relative flex-1 max-w-md hidden sm:block" data-testid="global-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        placeholder="Müşteri, tedarikçi, personel ara…  (Ctrl+K)" data-testid="global-search-input"
        className="h-9 w-full rounded-lg border border-border bg-background/60 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
      {open && q.trim() && (
        <div className="absolute left-0 right-0 mt-1 rounded-xl border border-border bg-card shadow-2xl overflow-hidden z-50" data-testid="global-search-results">
          {!data && <div className="px-4 py-3 text-sm text-muted-foreground">Yükleniyor…</div>}
          {data && results.length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground" data-testid="global-search-empty">Sonuç yok</div>}
          {results.map((r, i) => {
            const Icon = r.kind.icon;
            return (
              <button key={`${r.kind.key}-${r.item.id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(r)} onMouseEnter={() => setHi(i)}
                data-testid={`global-search-result-${r.kind.key}-${r.item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${i === hi ? "bg-accent" : "hover:bg-accent/60"}`}>
                <span className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-3.5 w-3.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{r.item.short_name || r.item.name}</span>
                  {r.item.short_name && <span className="block truncate text-[11px] text-muted-foreground">{r.item.name}</span>}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.kind.label}</span>
                {i === hi && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
